// "Acción de hoy" card content, keyed by giro + reto.
// Rendered as the last card of Screen3Results (after the 3 prompts / textos listos,
// before "Generar de nuevo").
//
// Every entry now has TWO variantes so the user never sees the same
// recommendation twice in a row (v2 "Recuerda combos" spec). The
// AccionDeHoy component tracks the last-shown variant index per giro+reto
// in localStorage and picks the OTHER variant on the next visit.
//
// Shape:
//   { source: <string>, variantes: [<text>, <text>] }
//
// Lookup strategy in getAccionDeHoy():
//   1. exact match on retoPorGiro[giro][reto]
//   2. fallback to genericoPorGiro[giro]
//   3. final fallback to genericoPorGiro.otro

const SOURCE_MENU_ENG =
  "📚 Basado en Menu Engineering · Kasavana & Smith · Cornell 1982";
const SOURCE_HBR_RETEN =
  "📚 Basado en Harvard Business Review: el costo de retener es 5x menor que el de adquirir un cliente nuevo";
const SOURCE_CIALDINI_ESCASEZ =
  "📚 Basado en Cialdini (Influence, 1984): la escasez percibida activa urgencia de compra";
const SOURCE_UNDERHILL =
  '📚 Basado en Paco Underhill (Why We Buy, 1999): los productos a la altura de los ojos venden hasta 40% más';
const SOURCE_META =
  "📚 Basado en Meta Business 2025: responder en menos de 1 hora aumenta la conversión 7x";
const SOURCE_CIALDINI_SOCIAL =
  "📚 Basado en Cialdini (Influence, 1984): la prueba social aumenta la conversión hasta 2x";

// Per-giro reto → { source, variantes: [v0, v1] }.
const retoPorGiro = {
  taqueria: {
    "Atraer clientes nuevos": {
      source: SOURCE_MENU_ENG,
      variantes: [
        `Toma una foto de tu taco más apetitoso ahora mismo.
Texto para publicar: "[Tu especialidad] recién hecha en [ciudad]. Disponible hasta las [hora]. Escríbenos 🌮"
Publica antes de las 12pm — es cuando la gente decide qué comer.`,
        `Graba 10 segundos del comal o la plancha en pleno movimiento.
Texto: "Así se está sirviendo hoy en [ciudad]. Solo hasta las [hora]. 🌮"
El sonido y el humo venden más que la mejor foto estática.`,
      ],
    },
    "Que regresen": {
      source: SOURCE_MENU_ENG,
      variantes: [
        `Escríbele a 5 clientes que no han venido en 2 semanas:
"¡Oye! Hace rato no te vemos. Hoy tenemos [tu especialidad] recién hecha. ¿Pasas? 🌮"
Personal, no masivo. Eso genera respuesta.`,
        `Al próximo cliente, pídele su WhatsApp para "avisarle cuando saques algo nuevo".
Con 20 números en una lista, tienes tu propia campaña gratuita cada semana.`,
      ],
    },
    "Vender más por visita": {
      source: SOURCE_MENU_ENG,
      variantes: [
        `Crea un combo hoy: tu taco más vendido + agua fresca = $[precio].
Escríbelo a mano en un papel y ponlo junto a la caja.
Sin diseño, sin app. Solo el letrero y el precio claro.`,
        `Entrena a quien atienda con una sola frase: "¿Le agrego una orden extra por $[precio] menos?"
Ese upsell verbal sube el ticket sin descuentos ni promociones.`,
      ],
    },
  },
  polleria: {
    "Llenar horas muertas": {
      source: SOURCE_MENU_ENG,
      variantes: [
        `Publica ahora: "⏰ Solo de [hora] a [hora]: Cuarto de pollo + tortillas = $[precio especial]. Solo hoy."
La urgencia de tiempo mueve a quien ya tenía ganas.`,
        `Manda un solo WhatsApp de estatus a tus 10 mejores clientes:
"Recién salió tanda nueva. Si pasas en la próxima hora, te doy tortillas de más 🐔"
La proximidad y la exclusividad los saca del sillón.`,
      ],
    },
    "Aumentar ticket": {
      source: SOURCE_MENU_ENG,
      variantes: [
        `Dile a cada cliente que llegue:
"¿Le agrego tortillas y salsa? Son $[precio] más y queda el combo completo."
Si 3 de cada 10 dicen sí, ya subiste el ticket del día.`,
        `Arma un letrero visible: "Con cualquier pollo, agrégale arroz + refresco por $[precio] menos que aparte."
El combo escrito convierte mejor que el que solo se ofrece verbalmente.`,
      ],
    },
    "Más clientes": {
      source: SOURCE_MENU_ENG,
      variantes: [
        `Graba 15 segundos del pollo saliendo del asador.
Sin texto. Sin edición. Solo el chisporroteo y el dorado.
Publícalo en historia de Facebook ahora.`,
        `Toma una foto cenital del mostrador lleno de pollos recién dorados.
Texto: "Así está el asador hoy en [ciudad]. Hasta que se acaben."
La abundancia visible detona el antojo.`,
      ],
    },
  },
  bar: {
    "Llenar entre semana": {
      source: SOURCE_MENU_ENG,
      variantes: [
        `Crea una razón fija para venir entre semana.
Ejemplo: "🍺 Miércoles de barril: segunda cerveza a mitad de precio de 6pm a 8pm. Solo miércoles."
Publica hoy y repítelo cada semana exactamente igual.`,
        `Invita a un músico local a tocar 45 minutos un martes o miércoles.
Publícalo hoy: "Martes acústico con [nombre]. Entrada libre desde las 8pm."
Un show pequeño llena mejor que 3 promociones sueltas.`,
      ],
    },
    "Aumentar consumo por mesa": {
      source: SOURCE_MENU_ENG,
      variantes: [
        `Entrena al mesero con 1 frase hoy:
"Cuando traigan la primera ronda, pregunta: ¿Les llevo botana para acompañar?"
Solo esa pregunta sube el ticket sin descuentos.`,
        `Diseña una tabla de "para compartir" con 3 botanas por $[precio].
Ponla al centro del menú y menciónala al recibir la mesa.
Los combos para compartir suben el ticket 20% en promedio.`,
      ],
    },
    "Conseguir clientes nuevos": {
      source: SOURCE_MENU_ENG,
      variantes: [
        `Video corto del bar ahora — la barra, las luces, el ambiente.
Texto: "Así está [nombre del bar] hoy. ¿Vienes? 🍺 [dirección]"
El FOMO es el mejor publicista de un bar.`,
        `Pídele a un cliente frecuente que grabe una historia dentro del bar y te etiquete.
Repostéala en tus historias. La recomendación visual de otro cliente convierte 3x más que tu propio contenido.`,
      ],
    },
  },
  gym: {
    "Conseguir miembros": {
      source: SOURCE_HBR_RETEN,
      variantes: [
        `Publica: "🔥 Clase de prueba GRATIS este [día] a las [hora]. Cupo para [número] personas. Escríbeme para apartar tu lugar."
El cupo limitado crea urgencia real.`,
        `A cada miembro actual, ofrécele: "Trae a un amigo esta semana. Si se inscribe, tú tienes un mes gratis."
Un referido cierra mejor que un anuncio a desconocidos.`,
      ],
    },
    "Evitar cancelaciones": {
      source: SOURCE_HBR_RETEN,
      variantes: [
        `Detecta quién no ha venido en 7+ días. Escríbele:
"¡Oye [nombre]! Te hemos extrañado. ¿Todo bien? Si necesitas cambiar tu horario, aquí estoy. 💪"
No vendas. Solo pregunta. Eso reactiva más que cualquier promo.`,
        `A cada miembro que lleve 3+ meses, mándale un audio corto (no mensaje escrito):
"¡Hola [nombre]! Solo quería agradecerte por seguir aquí. Sigue así 🙌"
El audio personal genera lealtad más que 10 promociones.`,
      ],
    },
    "Llenar horarios muertos": {
      source: SOURCE_HBR_RETEN,
      variantes: [
        `"Entrena de [hora floja] y paga $[precio] al mes — $[ahorro] menos que el horario normal. Solo esta semana."
Publícalo en historias y escríbele a 3 personas con horario flexible.`,
        `Ofrece una "clase temática" solo en horario muerto: yoga, funcional express, movilidad — cualquier cosa distinta.
Anúnciala 3 días antes con cupo limitado a 8 personas.`,
      ],
    },
  },
  ropa: {
    "Mover inventario estancado": {
      source: SOURCE_CIALDINI_ESCASEZ,
      variantes: [
        `Toma la prenda que más tiempo lleva sin venderse.
Fotográfiala bien puesta (en una persona, no colgada).
"Esta pieza lleva tiempo esperando a su dueña. Hoy la dejo en $[precio]. Una sola. Primera que llegue. 👗"`,
        `Arma un "outfit del día" combinando la prenda estancada con 2 piezas populares.
Publícalo como look completo, precio total.
Se vende porque deja de verse aislada.`,
      ],
    },
    "Que regresen": {
      source: SOURCE_CIALDINI_ESCASEZ,
      variantes: [
        `Escríbele a 5 clientas que compraron hace 3-4 semanas:
"¡Hola [nombre]! Llegó algo que creo que te va a encantar. ¿Te mando foto? 👗"
La curiosidad abre la conversación.`,
        `Manda una historia exclusiva a tu lista de "primeras clientas":
"Antes de subir esto al feed, se los muestro a ustedes. Apártenlo por WhatsApp."
La exclusividad genera compra impulsiva.`,
      ],
    },
    "Atraer clientas nuevas": {
      source: SOURCE_CIALDINI_ESCASEZ,
      variantes: [
        `Arma un outfit completo con prendas de tu tienda.
Fotográfialo junto con el precio total.
"Look completo por $[total]. Todo disponible hoy en [ciudad]."`,
        `Publica un "antes y después" — la misma persona con look básico y con tu outfit.
El contraste visual convierte mejor que fotos individuales de prendas.`,
      ],
    },
  },
  abarrotes: {
    "Productos que no rotan": {
      source: SOURCE_UNDERHILL,
      variantes: [
        `Identifica el producto con más tiempo en el estante.
Créale un combo con algo que sí rote.
Ponlo en la entrada con un letrero escrito a mano.`,
        `Muévelo a la caja como "producto sugerido" con un descuento pequeño.
El 30% de las decisiones de compra pasan en la caja — ahí se vende lo que no giraba.`,
      ],
    },
    "Competencia de Oxxo o tiendas grandes": {
      source: SOURCE_UNDERHILL,
      variantes: [
        `Escribe en cartón y ponlo en la entrada:
"Aquí te conocemos. Aquí te fiamos. Aquí eres bienvenido."
El Oxxo nunca podrá decir eso.`,
        `Ofrece "servicio a domicilio del barrio" por WhatsApp para pedidos arriba de $[precio].
Con 10 clientes fieles pidiendo así, tienes una ventaja que Oxxo no compite.`,
      ],
    },
    "Falta de clientes nuevos": {
      source: SOURCE_UNDERHILL,
      variantes: [
        `Pon tus 3 productos de mayor margen entre 90cm y 160cm de altura.
Los productos a la altura de los ojos venden hasta 40% más.
Tarda 5 minutos. Cuesta $0.`,
        `Coloca un producto de impulso (dulce, botana, bebida) a menos de 1 metro de la caja.
Cada mano en camino a pagar tiene que pasar frente a él.`,
      ],
    },
  },
  venta_redes: {
    "Que los que me ven me compren": {
      source: SOURCE_META,
      variantes: [
        `Abre tus redes ahora. Responde cada mensaje sin respuesta, cada comentario, cada DM. Usa el nombre si lo ves.
Responder en menos de 1 hora convierte 7x más.`,
        `A cada persona que dio like o comentó tu último post, mándale un DM:
"¡Hola! Vi que te gustó. ¿Quieres precio y disponibilidad?"
Ese primer contacto directo convierte más que cualquier landing.`,
      ],
    },
    "Conseguir más seguidores": {
      source: SOURCE_META,
      variantes: [
        `Pídele a un cliente satisfecho un mensaje de texto corto sobre su compra. Publícalo hoy como historia.
Un testimonio real vale más que 10 posts de producto.`,
        `Colabora hoy con otro vendedor local: intercambien una historia mencionándose.
Un cross-post entre dos cuentas del mismo pueblo/colonia suma seguidores reales.`,
      ],
    },
  },
  puesto: {
    "Que me descubran": {
      source: SOURCE_MENU_ENG,
      variantes: [
        `Al próximo cliente satisfecho: "¿Me harías un favor? Búscanos en Google como [nombre del puesto] y deja tu comentario."
Una reseña real atrae más que cualquier anuncio.`,
        `Pega un cartel visible con tu ubicación exacta y horario:
"Nos encuentras aquí de [hora] a [hora]. Búscanos en Google Maps 📍"
Muchos vecinos no saben que existes porque no te ven todos los días.`,
      ],
    },
    "Que regresen": {
      source: SOURCE_MENU_ENG,
      variantes: [
        `Dale un nombre memorable a tu platillo más pedido.
Escríbelo en un letrero: "El [nombre] — el que todos piden."
Los productos con nombre se recuerdan y se recomiendan.`,
        `Crea una tarjeta simple: "Al 5º pedido, uno va por la casa."
Un sistema de lealtad hecho a mano genera la 2ª y 3ª visita más rápido que cualquier promoción.`,
      ],
    },
    "Vender más por visita": {
      source: SOURCE_MENU_ENG,
      variantes: [
        `Publica al inicio de tu hora floja:
"⏰ Solo por 1 hora: [platillo] + [extra] = $[precio especial]. Estamos en [ubicación]."
La urgencia activa decisiones rápidas.`,
        `A cada cliente pregúntale: "¿Le agrego [extra] por $[precio]?"
El upsell verbal en el puesto tiene 20-30% de conversión sin costo extra.`,
      ],
    },
  },
};

// Fallback action per giro when the specific reto doesn't have a dedicated card.
const genericoPorGiro = {
  taqueria: {
    source: SOURCE_MENU_ENG,
    variantes: [
      `Toma una foto de tu taco más apetitoso ahora mismo y publícala con el precio en grande. La foto real vende más que cualquier diseño.`,
      `Graba 10 segundos del comal en pleno movimiento y súbelo a historia. El sonido del taco friendo mueve al antojo instantáneo.`,
    ],
  },
  polleria: {
    source: SOURCE_MENU_ENG,
    variantes: [
      `Publica al inicio de tu hora floja: "⏰ Solo hoy: cuarto de pollo con tortillas a $[precio]." La urgencia mueve al que ya tenía ganas.`,
      `Toma una foto cenital del mostrador con los pollos recién dorados. La abundancia visible dispara el antojo.`,
    ],
  },
  bar: {
    source: SOURCE_MENU_ENG,
    variantes: [
      `Graba 15 segundos del bar ahora mismo — la barra, las luces, el ambiente. Súbelo a historia con la dirección. El FOMO es tu mejor publicista.`,
      `Publica una foto de la barra llena antes de las 8pm con el texto: "Así está hoy [nombre del bar]. ¿Vienes?" La prueba social visual llena más mesas.`,
    ],
  },
  gym: {
    source: SOURCE_HBR_RETEN,
    variantes: [
      `Escríbele hoy a 3 miembros que no vienen hace 7 días: "¿Todo bien? Aquí seguimos, si necesitas cambiar horario dime." Reactivar cuesta 5x menos que captar.`,
      `Manda un audio de 20 segundos a tu grupo de WhatsApp motivando a venir esta semana. La voz personal reactiva mejor que cualquier texto.`,
    ],
  },
  ropa: {
    source: SOURCE_CIALDINI_ESCASEZ,
    variantes: [
      `Toma la prenda con más tiempo en la percha. Fotografíala en una persona (no colgada). Publícala como "última pieza en esta talla" — la escasez vende.`,
      `Publica un "outfit del día" combinando 3 prendas con precio total. Los looks completos convierten mejor que fotos aisladas.`,
    ],
  },
  abarrotes: {
    source: SOURCE_UNDERHILL,
    variantes: [
      `Reacomoda tus 3 productos de mayor margen a la altura de los ojos (90cm-160cm). Toma 5 minutos, cuesta $0 y sube ventas hasta 40%.`,
      `Coloca un producto de impulso pequeño a menos de 1 metro de la caja. Cada mano que pague va a pasar frente a él.`,
    ],
  },
  venta_redes: {
    source: SOURCE_META,
    variantes: [
      `Publica una historia de tu producto más vendido con el precio en grande y cómo pedirlo. No escondas el precio — quien pregunta ya está listo para comprar.`,
      `Mándale un DM personalizado a cada persona que dio like o comentó tu último post: "¿Quieres precio y disponibilidad?"`,
    ],
  },
  puesto: {
    source: SOURCE_MENU_ENG,
    variantes: [
      `Al próximo cliente satisfecho pídele que te busque en Google y deje un comentario con estrellas. Una reseña real atrae más que cualquier anuncio.`,
      `Escribe un letrero pequeño con tu ubicación exacta y horario. Muchos vecinos no saben que estás ahí porque no te ven todos los días.`,
    ],
  },
  cafeteria: {
    source: SOURCE_MENU_ENG,
    variantes: [
      `A cada cliente que pida solo el café, di: "¿Le agrego [pan/galleta]? Son $[precio] más y queda el combo."
El upsell verbal convierte 20-30% de las veces. Sin costo extra.`,
      `Arma un combo "café + algo dulce" al mismo precio de un café tamaño grande. Se vende porque la gente percibe más valor sin sentir que gasta más.`,
    ],
  },
  estetica: {
    source: SOURCE_HBR_RETEN,
    variantes: [
      `Pídele permiso a tu próximo cliente para tomar foto del resultado.
Publica: "Esto es lo que pasa en [tiempo] con las manos correctas. ¿Agendas? ✂️"
La prueba visual vende más que cualquier lista de precios.`,
      `Antes de que el cliente se vaya, agenda su próxima cita.
"Nos vemos en 3 semanas — ¿te apunto el mismo día y hora?"
Cerrar la próxima visita en la actual sube la retención al 70%.`,
    ],
  },
  tortilleria: {
    source: SOURCE_MENU_ENG,
    variantes: [
      `A cada cliente hoy di:
"Si llevas 2 kilos te los dejo en $[precio combo]."
Si 3 de cada 10 aceptan, ya subiste el ticket del día.`,
      `Manda un WhatsApp a tus 5 clientes de fondas/taquerías: "¿Cuántos kilos necesitas mañana? Te los aparto." El pedido anticipado los amarra a tu tortilla.`,
    ],
  },
  otro: {
    source: SOURCE_CIALDINI_SOCIAL,
    variantes: [
      `Al cliente más satisfecho que tengas, dile:
"¿Me harías un favor? Búscanos en Google y deja un comentario."
Una reseña con 5 estrellas atrae más clientes que cualquier anuncio.`,
      `Pídele a un cliente satisfecho un breve testimonio por WhatsApp y publícalo hoy como historia. Un testimonio real vale más que cualquier lista de beneficios.`,
    ],
  },
};

function getEntry(businessType, reto) {
  const perGiro = retoPorGiro[businessType];
  if (perGiro && reto && perGiro[reto]) return perGiro[reto];
  return genericoPorGiro[businessType] || genericoPorGiro.otro;
}

// Returns { text, source, variantIdx, lookupKey }.
// lastVariantIdx is the previously shown variant index for this giro+reto
// (from localStorage). We flip to the OTHER variant so the user never sees
// the same recommendation twice in a row.
export function getAccionDeHoy(businessType, reto, lastVariantIdx) {
  const entry = getEntry(businessType, reto);
  const variants = entry.variantes;
  // Pick the variant that is NOT the last one shown. When there is no
  // previous record (undefined/null/-1), default to 0.
  let idx;
  if (lastVariantIdx == null || lastVariantIdx < 0 || lastVariantIdx >= variants.length) {
    idx = 0;
  } else {
    idx = (lastVariantIdx + 1) % variants.length;
  }
  return {
    text: variants[idx],
    source: entry.source,
    variantIdx: idx,
    lookupKey: `${businessType || "otro"}::${reto || "__generic__"}`,
  };
}
